<template>
  <!-- Форма «Написать в поддержку» (f01-f24): модалка 832, затемнение 65% на весь экран -->
  <v-dialog
    :value="open"
    width="832"
    overlay-opacity="0.65"
    content-class="support-form-dialog"
    @input="onToggle"
  >
    <div class="support-form">
      <header class="support-form__head">
        <v-icon size="22" :class="step === 'form' ? 'support-form__head-pen' : 'support-form__head-ok'">
          {{ step === 'form' ? 'mdi-pencil' : 'mdi-check-circle-outline' }}
        </v-icon>
        <span class="support-form__title">{{ title }}</span>
        <button
          type="button"
          class="support-form__close"
          aria-label="Закрыть"
          @click="close"
        >
          <v-icon size="22">
            mdi-close
          </v-icon>
        </button>
      </header>

      <!-- Форма -->
      <template v-if="step === 'form'">
        <div class="support-form__body">
          <div class="support-form__label">
            С чем нужна помощь?
            <span v-if="!category" class="support-form__required">*обязательно выберите категорию обращения</span>
          </div>
          <category-tiles v-model="category" />

          <order-picker v-if="category === 'orders_delivery'" v-model="order" />

          <!-- «Встречает» после выбора категории: вопрос и подсказка (f03, f20-f24) -->
          <div v-else-if="greet" class="support-form__greet">
            <span class="support-form__greet-icon">
              <v-icon size="20">{{ categoryMeta.icon }}</v-icon>
            </span>
            <div>
              <div class="support-form__greet-question">
                {{ greet.question }}
              </div>
              <div class="support-form__greet-hint">
                {{ greet.hint }}
              </div>
            </div>
          </div>

          <photo-field
            ref="field"
            v-model="text"
            :disabled="!category"
            @photos="photoCount = $event"
            @uploading="uploading = $event"
          />

          <!-- Не отправилось: всё введённое остаётся (Q23) -->
          <div v-if="errorText" class="support-form__error">
            <v-icon size="20">
              mdi-alert-circle-outline
            </v-icon>
            <span class="support-form__error-text">{{ errorText }}</span>
            <v-btn
              depressed
              small
              class="support-form__retry text-normalize"
              :loading="sending"
              @click="send"
            >
              Отправить ещё раз
            </v-btn>
          </div>
        </div>

        <footer class="support-form__foot">
          <span class="support-form__foot-note">{{ footNote }}</span>
          <v-btn
            depressed
            height="48"
            width="160"
            class="support-form__cancel text-normalize"
            @click="close"
          >
            Отмена
          </v-btn>
          <v-btn
            depressed
            height="48"
            width="220"
            class="support-form__send text-normalize"
            :class="{ 'support-form__send--inactive': !sendable }"
            :loading="sending"
            :disabled="uploading"
            @click="send"
          >
            Отправить
            <!-- Контурная на пустом поле, залитая с текстом; ширина не меняется -->
            <v-icon right size="18">
              {{ text.trim() ? 'mdi-send' : 'mdi-send-outline' }}
            </v-icon>
          </v-btn>
        </footer>
      </template>

      <!-- Отправлено (f05) или идея отправлена (f23) -->
      <template v-else>
        <div class="support-form__done">
          <!-- Самолётик летит в галочку один раз и покачивается (П24); у идеи - искры (П25) -->
          <div v-if="step === 'sent'" class="sent-art" aria-hidden="true">
            <img
              :src="images.sentBase"
              :srcset="`${images.sentBase2x} 2x`"
              alt=""
            >
            <img
              class="sent-art__plane"
              :src="images.sentPlane"
              :srcset="`${images.sentPlane2x} 2x`"
              alt=""
            >
          </div>
          <sparkles v-else />
          <div class="support-form__done-title">
            {{ step === 'sent' ? `Обращение №${created.number} отправлено` : 'Спасибо за вашу идею!' }}
          </div>
          <div class="support-form__done-text">
            {{ step === 'sent'
              ? 'Ответ придёт в «Обращения филиала» и в уведомления.'
              : 'Мы обязательно её рассмотрим. Если возьмём в работу - напишем вам в «Обращения филиала», чтобы вы первыми узнали' }}
          </div>
          <span class="support-form__done-chip">
            <v-icon size="16">{{ createdMeta.icon }}</v-icon>
            {{ doneChip }}
          </span>
        </div>
        <footer class="support-form__foot support-form__foot--center">
          <template v-if="step === 'sent'">
            <v-btn
              depressed
              height="48"
              width="160"
              class="support-form__cancel text-normalize"
              @click="close"
            >
              Закрыть
            </v-btn>
            <v-btn
              depressed
              height="48"
              width="240"
              class="support-form__send text-normalize"
              @click="openCreated"
            >
              Открыть обращение
              <v-icon right size="18">
                mdi-open-in-new
              </v-icon>
            </v-btn>
          </template>
          <v-btn
            v-else
            depressed
            height="48"
            width="220"
            class="support-form__send text-normalize"
            @click="close"
          >
            Готово
            <v-icon right size="18">
              mdi-check
            </v-icon>
          </v-btn>
        </footer>
      </template>
    </div>
  </v-dialog>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import { DateTime } from 'luxon'
  import supportApi, { supportErrorCode, supportErrorText } from '@/api/support'
  import RouteName from '@/models/enums/RouteName'
  import CategoryTiles from './CategoryTiles.vue'
  import OrderPicker from './OrderPicker.vue'
  import PhotoField from './PhotoField.vue'
  import { canSend, category, sendAction } from '../form'
  import { photos } from '../format'
  import sentBase from '@/assets/support/anim/sent-base.png'
  import sentBase2x from '@/assets/support/anim/sent-base@2x.png'
  import sentPlane from '@/assets/support/anim/sent-plane.png'
  import sentPlane2x from '@/assets/support/anim/sent-plane@2x.png'
  import Sparkles from './Sparkles.vue'

  function emptyState () {
    return {
      step: 'form',
      category: null,
      order: { order: null, orderNumber: null },
      text: '',
      photoCount: 0,
      uploading: false,
      sending: false,
      errorText: null,
      created: null,
    }
  }

  export default {
    name: 'SupportFormDialog',
    components: { CategoryTiles, OrderPicker, PhotoField, Sparkles },
    data () {
      return { ...emptyState(), images: { sentBase, sentBase2x, sentPlane, sentPlane2x } }
    },
    computed: {
      ...mapState({ open: state => state.support.formOpen }),
      ...mapGetters({ presence: 'support/presence' }),
      title () {
        if (this.step === 'sent') return 'Ваше обращение отправлено!'
        if (this.step === 'idea') return 'Ваша идея отправлена!'

        return 'Написать в поддержку'
      },
      categoryMeta () {
        return category(this.category)
      },
      greet () {
        return this.category ? this.categoryMeta.greet : null
      },
      sendable () {
        return canSend(this.formState)
      },
      formState () {
        return { category: this.category, order: this.order.order, orderNumber: this.order.orderNumber, text: this.text }
      },
      // Вне графика отправлять можно, но честно говорим, когда ответят (Q23).
      footNote () {
        const presence = this.presence
        if (presence && !presence.online) {
          return presence.nextOnlineAt
            ? `Поддержка не на линии · ответим после ${DateTime.fromISO(presence.nextOnlineAt).setZone('Europe/Moscow').toFormat('HH:mm')} МСК`
            : 'Поддержка не на линии · ответ придёт в уведомления'
        }

        return 'Ответ придёт в «Обращения филиала» и в уведомления'
      },
      createdMeta () {
        return category(this.created?.category)
      },
      doneChip () {
        if (this.step === 'idea') return `Предложить идею · №${this.created.number}`
        const count = photos(this.created.photoCount)

        return count ? `${this.createdMeta.label} · ${count}` : this.createdMeta.label
      },
    },
    methods: {
      onToggle (value) {
        if (!value) this.close()
      },
      // Закрыли - всё сначала: следующий раз форма пустая.
      close () {
        this.$store.dispatch('support/closeForm')
        if (this.$refs.field) this.$refs.field.reset()
        Object.assign(this.$data, emptyState())
      },
      async send () {
        const action = sendAction(this.formState)
        if (action === 'blocked' || this.sending) return
        // Пустое поле: фокус в него, ничего не шлём (Q22).
        if (action === 'focus') {
          this.$refs.field.focus()
          return
        }

        this.sending = true
        this.errorText = null
        try {
          const photoIds = this.$refs.field.photoIds()
          const { appeal } = await supportApi.create({
            category: this.category,
            orderId: this.order.order?.id || null,
            orderNumber: this.order.order ? null : this.order.orderNumber,
            text: this.text.trim(),
            attachmentIds: photoIds,
          })
          this.created = { id: appeal.id, number: appeal.number, category: appeal.category, photoCount: photoIds.length }
          this.step = appeal.type === 'idea' ? 'idea' : 'sent'
          this.$store.commit('support/formSent')
        } catch (e) {
          // Нет сети или сервис лёг - «Не отправилось…»; правило нарушено (409) - текст бэка.
          const code = supportErrorCode(e)
          this.errorText = code === 'network' || code.startsWith('http_5') || code === 'crm_unavailable'
            ? 'Не отправилось… Проверьте связь и попробуйте ещё раз'
            : supportErrorText(e, 'Не отправилось…')
        } finally {
          this.sending = false
        }
      },
      openCreated () {
        const id = this.created.id
        this.close()
        this.$router.push({ name: RouteName.SupportAppeal, params: { id } })
      },
    },
  }
</script>

<style scoped lang="less">

/* П24. Самолётик «отправлено»: полёт в галочку один раз, потом покачивание. Точки - из шейдера
   sent-fly.glsl, сдвиги в % картинки: одинаково на 200 и 400 px */
.sent-art {
  position: relative;
  width: 200px;
  aspect-ratio: 1;
  animation: sent-pulse 0.45s ease-in-out 1.45s both;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }
}

/* «Принято»: самолётик попал в галочку - картинка на миг вырастает на 6% */
@keyframes sent-pulse {
  50% {
    transform: scale(1.06);
  }
}

.sent-art__plane {
  transform-origin: 34.47% 56.25%;
  animation:
    sent-trip 2.4s linear 0.5s both,
    sent-bob 1.2s ease-in-out 2.9s infinite;
}

@keyframes sent-trip {
  0% { transform: translate(0, 0) scale(1); opacity: 1; }
  4.17% { transform: translate(1.13%, -1.87%) scale(0.991); }
  8.33% { transform: translate(4.2%, -6.73%) scale(0.95); }
  12.5% { transform: translate(8.72%, -13.29%) scale(0.872); }
  16.67% { transform: translate(14.21%, -20.33%) scale(0.758); }
  20.83% { transform: translate(20.17%, -26.81%) scale(0.618); }
  25% { transform: translate(26.12%, -32.06%) scale(0.465); }
  29.17% { transform: translate(31.58%, -35.81%) scale(0.315); opacity: 1; }
  33.33% { transform: translate(36.07%, -38.13%) scale(0.185); opacity: 0.95; }
  37.5% { transform: translate(39.11%, -39.3%) scale(0.094); opacity: 0.14; }
  41.67% { transform: translate(40.23%, -39.65%) scale(0.06); opacity: 0; }
  41.68% { transform: translate(-25.39%, 3.91%) scale(1); opacity: 0; }
  75% { transform: translate(-25.39%, 3.91%) scale(1); opacity: 0; animation-timing-function: ease-in-out; }
  100% { transform: translate(0, 0) scale(1); opacity: 1; }
}

@keyframes sent-bob {
  0%,
  50%,
  100% { transform: translateY(0); animation-timing-function: cubic-bezier(0.22, 0.61, 0.36, 1); }
  25% { transform: translateY(0.59%); animation-timing-function: ease-in; }
  75% { transform: translateY(-0.59%); animation-timing-function: ease-in; }
}

/* Без движения - самолётик стоит на облачке */
@media (prefers-reduced-motion: reduce) {
  .sent-art,
  .sent-art__plane {
    animation: none;
  }
}
@import '../tokens.less';

::v-deep .support-form-dialog {
  overflow: hidden;
  border-radius: 8px;
}

.support-form {
  background: @fakt-white;
}

.support-form__head {
  display: flex;
  gap: 12px;
  align-items: center;
  height: 76px;
  padding: 0 24px;
  background: @fakt-dark;
}

.support-form__head-pen {
  color: @fakt-orange !important;
}

.support-form__head-ok {
  color: @fakt-green !important;
}

.support-form__title {
  flex: 1;
  font-size: 18px;
  font-weight: 500;
  color: @fakt-white;
}

.support-form__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  .v-icon {
    color: @fakt-on-dark-muted !important;
  }
}

.support-form__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
}

.support-form__label {
  margin-bottom: -8px;
  font-size: 13px;
  font-weight: 500;
  color: @fakt-text;
}

.support-form__required {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 400;
  color: @fakt-pink;
}

.support-form__greet {
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 14px 16px;
  border-radius: 10px;
  background: @fakt-cream-plate;
}

.support-form__greet-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: @fakt-white;

  .v-icon {
    color: @fakt-orange !important;
  }
}

.support-form__greet-question {
  font-size: 14px;
  font-weight: 500;
  color: @fakt-text;
}

.support-form__greet-hint {
  font-size: 13px;
  color: @fakt-text-2;
}

.support-form__error {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px 12px 10px 16px;
  border-radius: 10px;
  font-size: 13px;
  color: @fakt-pink;
  background: rgba(233, 30, 99, .08);

  .v-icon {
    color: @fakt-pink !important;
  }
}

.support-form__error-text {
  flex: 1;
}

.support-form__retry {
  color: @fakt-white !important;
  background-color: @fakt-pink !important;
}

.support-form__foot {
  display: flex;
  gap: 12px;
  align-items: center;
  min-height: 84px;
  padding: 0 24px;
  border-top: 1px solid @fakt-divider;

  &--center {
    justify-content: center;
  }
}

.support-form__foot-note {
  flex: 1;
  font-size: 12px;
  color: @fakt-text-3;
}

.support-form__cancel {
  border-radius: 10px;
  font-size: 14px;
  background-color: @fakt-surface !important;
}

.support-form__send {
  border-radius: 10px;
  font-size: 14px;
  color: @fakt-white !important;
  background-color: @fakt-orange !important;

  &--inactive {
    opacity: .4;
  }
}

.support-form__done {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  padding: 32px 24px;
  text-align: center;
}

.support-form__done-title {
  margin-top: 8px;
  font-size: 20px;
  font-weight: 500;
  color: @fakt-text;
}

.support-form__done-text {
  max-width: 520px;
  font-size: 14px;
  color: @fakt-text-2;
}

.support-form__done-chip {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  height: 30px;
  margin-top: 4px;
  padding: 0 12px;
  border-radius: 100px;
  font-size: 13px;
  color: @fakt-text;
  background: @fakt-surface;
}
</style>
